import React, { useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import CCText from "@/Atoms/CCText";
import type { ClubFunChallenge } from "@/Hooks/ClubsApi/useClubFunChallenge";
import {useMediaQuery} from "@/Hooks/useMediaQuery/useMediaQuery";

export type ChallengeState = "live" | "ended" | "expired";

/** Mirrors the server predicate: active == ended_at IS NULL AND expires_at > now.
 *  `ended` wins over `expired` — a finished race reads as finished, not stale. */
export function challengeState(
  challenge: ClubFunChallenge,
  nowMs: number,
): ChallengeState {
  if (challenge.ended_at) return "ended";
  if (new Date(challenge.expires_at).getTime() <= nowMs) return "expired";
  return "live";
}

function mmss(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * A club fun challenge (Puzzle Royale) rendered inline in the club chat stream.
 * Members join from here without leaving the chat.
 *
 * The countdown is intentionally NOT InviteCountdown: that component turns
 * critical below 15 minutes, and a royale lobby only lives 15 minutes, so it would
 * render red from birth. Here only the final minute is critical.
 */
export default function FunChallengeCard({
  challenge,
  joining,
  onJoin,
}: {
  challenge: ClubFunChallenge;
  joining?: boolean;
  onJoin: (shareCode: string) => void;
}) {
  const {isMobile} = useMediaQuery();
  const [now, setNow] = useState(() => Date.now());

  const expiresMs = new Date(challenge.expires_at).getTime();
  const state = challengeState(challenge, now);

  // Tick only while live — a finished/expired card is static.
  useEffect(() => {
    if (state !== "live") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  const remaining = expiresMs - now;
  const critical = remaining <= 60 * 1000;

  const timerSec = Number(challenge.settings?.timerSec) || 0;
  const difficulty = String(challenge.settings?.difficulty || "");
  const summary = [
    timerSec ? `${timerSec}s race` : null,
    difficulty || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <View
      testID="fun-challenge-card"
      style={{
        // Keep activity cards aligned with incoming challenge/tournament cards
        // in the shared chat timeline.
        alignSelf: "flex-start",
        width: isMobile ? "90%" : "50%",
        backgroundColor: "#EAF6F4",
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "rgba(61,171,158,0.35)",
        padding: 13,
        marginVertical: 6,
      }}>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <CCText style={{ fontSize: 15, marginRight: 8 }}>⚡</CCText>
        <View style={{ flex: 1 }}>
          <CCText
            style={{
              fontSize: 10.5,
              fontWeight: "700",
              color: "#2C7F76",
              textTransform: "uppercase",
              letterSpacing: 0.06,
            }}>
            Puzzle Royal
          </CCText>
          <CCText
            fontWeight="thunder"
            style={{ fontSize: 16, color: "#262322" }}>
            Puzzle Royal is created
          </CCText>
        </View>
        {state === "live" ? (
          <CCText
            style={{
              fontSize: 13,
              fontWeight: "700",
              color: critical ? "#CD2C39" : "#2C7F76",
              fontVariant: ["tabular-nums"],
            }}>
            {mmss(remaining)}
          </CCText>
        ) : null}
      </View>

      {summary ? (
        <CCText style={{ fontSize: 12.5, color: "#6B584D", marginTop: 6 }}>
          A new club puzzle race is ready · {summary}
        </CCText>
      ) : null}

      {state === "live" ? (
        <Pressable
          testID="fun-challenge-join-btn"
          disabled={joining}
          onPress={() => {
            if (joining) return;
            onJoin(challenge.share_code);
          }}
          style={{
            backgroundColor: joining ? "#B7D9D3" : "#3DAB9E",
            borderRadius: 10,
            paddingVertical: 10,
            alignItems: "center",
            marginTop: 11,
          }}>
          <CCText style={{ fontSize: 13.5, fontWeight: "600", color: "#fff" }}>
            {joining ? "Joining…" : "Join race"}
          </CCText>
        </Pressable>
      ) : (
        <CCText style={{ fontSize: 12.5, color: "#8E844E", marginTop: 9 }}>
          {state === "ended" ? "Race finished" : "Expired"}
        </CCText>
      )}
    </View>
  );
}
